import React, { useEffect, useMemo, useRef } from 'react';
import DataGrid, { Column, MasterDetail } from 'devextreme-react/data-grid';
import { Editing } from 'devextreme-react/gantt';
import { Button } from 'devextreme-react/button';
import ScrollView from 'devextreme-react/scroll-view';
import { formatMessage } from 'devextreme/localization';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import styles from './inputModalSemiProduct.module.scss';
import { columnDataGrid, columnDetailGrid1, columnDetailGrid2 } from './columnDataGrid';
import { productionStoringNames } from '../constants';
import convertWeightUnit, { isWeightUnit } from '../../../utils/weight_converter';
import PopupMVP from '../../PopupMVP';

const saveAllWasteDtoMapper = (
  operationPieceAllWasteSaveDtos,
  zdvlLineNumId = 0
) => (
  operationPieceAllWasteSaveDtos.map((piece) => ({
    allWastes: piece.allWastes.map((waste) => ({
      matnrComp: waste.matnrComp,
      matnrCompText: waste.matnrCompText,
      semiQuantityOff: waste.quantitySemiOff,
      weightOff: waste.weightOff,
      weightUnitComp: waste.unitOfMeasure,
      weightCurrent: waste.weightCurrent,
      semiQuantityUnitOff: waste.semiQuantityUnitOff
    })),
    pieceNumId: piece.pieceNumId,
    zdvlLineNumId
  }))
);

const InputSemiProductWastesPopup = ({
  inputOperationReducer,
  saveOperation,
  getSemiProducts,
  storingName,
  setVisibleModalError,
  visibleModalInput,
  dataGridFilter,
  loadingModal,
  plantId,
  onClose
}) => {
  const updatedData = useRef();
  const dataGridRef = useRef();
  const dataWasteGridRef = useRef();
  const isSaving = useRef(false);

  const changedRows = [];

  const dataSource = useMemo(() => (
    inputOperationReducer.operationMaterials.operationPieces
  ), [inputOperationReducer.operationMaterials.operationPieces]);

  useEffect(() => {
    const storeData = inputOperationReducer.operationMaterials.operationPieces;
    if (storeData) updatedData.current = storeData;
  }, [inputOperationReducer.operationMaterials.operationPieces]);

  const isEditable = (item) =>
    storingName === productionStoringNames.dataGridInputAllWaste
    && !!item.editable;

  const calculateQuantityOff = (newData, value) => {
    // eslint-disable-next-line no-param-reassign
    newData.weightOff = value;
  };

  const calculatePercentSemiOff = (newData, value, currentRowData) => {
    const { unitOfMeasure, semiQuantityUnitOff } = currentRowData;
    // проверяем обе ли весовые
    if (isWeightUnit(semiQuantityUnitOff) && isWeightUnit(unitOfMeasure)) {
      changedRows.push(currentRowData.matnrComp);
      // eslint-disable-next-line no-param-reassign
      newData.percentOff = value;

      const rowValue = (convertWeightUnit(value, semiQuantityUnitOff, unitOfMeasure) / 100)
        * dataSource.filter(
          (item) => item.pieceNumId === currentRowData.pieceNumId
        )[0].quantity;

      // eslint-disable-next-line no-param-reassign
      newData.weightOff = parseFloat(rowValue.toFixed(3));
      // eslint-disable-next-line no-param-reassign
      newData.quantitySemiOff = parseFloat(convertWeightUnit(
        newData.weightOff,
        unitOfMeasure,
        semiQuantityUnitOff
      ).toFixed(3));
    } else {
      // eslint-disable-next-line no-param-reassign
      newData.weightOff = value;
    }
  };

  const calculateQuantitySemiOff = (newData, value, currentRowData) => {
    const { unitOfMeasure, semiQuantityUnitOff } = currentRowData;
    // проверяем обе ли весовые
    if (isWeightUnit(semiQuantityUnitOff) && isWeightUnit(unitOfMeasure)) {
      changedRows.push(currentRowData.matnrComp);
      // eslint-disable-next-line no-param-reassign
      newData.quantitySemiOff = value;
      // eslint-disable-next-line no-param-reassign
      newData.weightOff = convertWeightUnit(
        value,
        semiQuantityUnitOff,
        unitOfMeasure
      );
      // eslint-disable-next-line no-param-reassign
      newData.percentOff = Math.round(newData.quantitySemiOff
      // eslint-disable-next-line no-mixed-operators,max-len
      * 100 / (dataSource.filter((item) => item.pieceNumId === currentRowData.pieceNumId)[0].quantity));
    } else {
      // eslint-disable-next-line no-param-reassign
      newData.quantitySemiOff = value;
    }
  };

  const onSaveClickHandler = async () => {
    const dataGrid = dataWasteGridRef.current.instance;
    let isValidationWaste = true;
    if (dataGrid.hasEditData()) await dataGrid.saveEditData();
    updatedData.current.forEach((entry) => {
      if (!isWeightUnit(entry.unit)) {
        entry.allWastes.forEach((item) => {
          if ((item.quantitySemiOff !== 0 && item.weightOff === 0)
            || (item.quantitySemiOff === 0 && item.weightOff !== 0)) {
            isValidationWaste = false;
          }
        });
      }
    });
    if (isValidationWaste) {
      saveOperation({
        operationPieceAllWasteSaveDtos: saveAllWasteDtoMapper(
          updatedData.current
        ),
        operationType: 5
      });
      isSaving.current = true;
    } else {
      setVisibleModalError(true);
    }
  };

  useEffect(() => {
    if (!inputOperationReducer.loading && !inputOperationReducer.error && isSaving.current) {
      isSaving.current = false;
      onClose();
      getSemiProducts({
        ...dataGridFilter,
        plantId
      });
    }
  }, [
    inputOperationReducer.loading,
    inputOperationReducer.error,
    isSaving,
    dataGridFilter,
    getSemiProducts,
    onClose,
    plantId
  ]);

  const renderAllWasteGrid = ({ data }) => {
    const semiQuantityUnitOff = data.unit;
    const wasteData = data.allWastes.map((waste) => ({
      ...waste,
      semiQuantityUnitOff
    }));
    const unitSemiProduct = dataSource
      .filter((item) => item.pieceNumId === wasteData[0].pieceNumId)
      .map((item) => item.unit)[0];
    return (
      <DataGrid dataSource={wasteData}
                ref={dataWasteGridRef}
                showBorders
                columnAutoWidth
                keyExpr="matnrComp"
                onRowUpdated={(e) => {
                  const newAllWastes = e.component.getDataSource().items();
                  updatedData.current = updatedData.current.map((d) =>
                    d.pieceNumId === newAllWastes[0].pieceNumId
                      ? { ...d, allWastes: newAllWastes }
                      : d);
                }}
      >
        <Editing allowUpdating
                 selectTextOnEditStart
                 mode="cell"
        />
        {columnDetailGrid1.map((item) => (
          <Column dataField={item.dataField}
                  caption={item.caption}
                  visible={!item.notVisible}
                  key={item.dataField}
                  cellRender={item.cellRender}
                  allowEditing={!!item.editable}
                  dataType={item.dataType}
                  cssClass={item.editable ? styles.editableCell : item.cssClass}
                  width={item.width ? item.width : undefined}
          />
        ))}
        <Column dataField="weightOff"
                caption={`Кол-во выпуска отходов, ${wasteData[0].unitOfMeasureShortName}`}
                dataType="number"
                editable={!isWeightUnit(unitSemiProduct)}
                allowEditing={!isWeightUnit(unitSemiProduct)}
                cssClass={!isWeightUnit(unitSemiProduct) && styles.editableCell}
                setCellValue={calculateQuantityOff}
        />
        {columnDetailGrid2.map((item) => (
          <Column dataField={item.dataField}
                  caption={item.caption}
                  visible={!item.notVisible}
                  key={item.dataField}
                  cellRender={item.cellRender}
                  allowEditing={!!item.editable}
                  dataType={item.dataType}
                  cssClass={item.editable ? styles.editableCell : item.cssClass}
                  width={item.width ? item.width : undefined}
          />
        ))}
        <Column dataField="percentOff"
                caption="% отходов"
                dataType="number"
                editable
                cssClass={styles.editableCell}
                setCellValue={calculatePercentSemiOff}
        />
        <Column dataField="quantitySemiOff"
                caption={`Кол-во списания ПФ, ${data.unitShortName}`}
                dataType="number"
                editable
                cssClass={styles.editableCell}
                setCellValue={calculateQuantitySemiOff}
        />
      </DataGrid>
    );
  };

  return (
    <PopupMVP visible={visibleModalInput}
              onHiding={onClose}
              title={'Ввод отходов'}
              width={'90%'}
              height={'auto'}
              position={'center'}
  // fullScreen
    >
      <div className="formData">
        <ScrollView elementAttr={{ class: styles.itemScroll }}
                    direction="vertical"
                    // showScrollbar="always"
                    height="calc(100% - 85px)"
                    scrollByContent
                    scrollByThumb
        >
          <DataGrid ref={dataGridRef}
                      dataSource={dataSource}
                      showBorders
                      columnAutoWidth
                      elementAttr={{ class: `${styles.dataGrid} shipment` }}
            >
            <MasterDetail enabled={storingName === productionStoringNames.dataGridInputAllWaste}
                            render={renderAllWasteGrid}
                            autoExpandAll
              />
            {columnDataGrid.map((item) => (
              <Column dataField={item.dataField}
                        caption={item.caption}
                        visible={!item.notVisible}
                        key={item.dataField}
                        cellRender={item.cellRender}
                        allowEditing={isEditable(item)}
                        dataType={item.dataType}
                        cssClass={isEditable(item) ? styles.editableCell : item.cssClass}
                        width={item.width ? item.width : undefined}
              />
            ))}
            <Column dataField={'weightOff'}
                      caption={'Кол-во списания'}
                      visible={storingName !== productionStoringNames.dataGridInputAllWaste}
                      key={'weightOff'}
                      allowEditing
                      dataType={'number'}
                      cssClass={styles.editableCell}
            />
          </DataGrid>
        </ScrollView>
        <div className="formButton">
          <Button text={formatMessage('textButton').reject}
                  type="default"
                  stylingMode="text"
                  onClick={onClose}
        />
          <Button type="default"
                  stylingMode="contained"
                  onClick={onSaveClickHandler}
                  width={260}
        >
            <LoadIndicator className="indicator" visible={loadingModal} />
            <span>{formatMessage('textButton').save}</span>
          </Button>
        </div>
      </div>
    </PopupMVP>
  );
};

export default InputSemiProductWastesPopup;
